import { useMemo } from "react";
import type { TripType } from "@b2bportal/air-shopping-api";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type IFareData,
  type IFlightFares,
} from "@b2bportal/core-types";
import { useFareKeys } from "@tenant/flights";
import clsx from "clsx";
import { useFlightSlot } from "../../../../slots";
import defaultStyles from "./FlightFareGrid.module.scss";

export interface TFlightFareGrid {
  className?: string;
  fares: IFlightFares;
  fareShelfFilter?: number[];
  tripType: TripType;
  tagsLimit?: number;
  onFareGridClick: (fare: IFareData) => void;
}

export const FlightFareGrid = ({
  fares,
  tripType,
  tagsLimit = 2,
  onFareGridClick,
  fareShelfFilter,
  className,
}: TFlightFareGrid) => {
  const styles = useFlightStyles(
    CoreFlightsComponent.FlightFareGrid,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(styles, CoreFlightsComponent.FlightFareGrid);

  const FlightSlot = useFlightSlot();
  const fareKeys = useFareKeys();
  const grid = useMemo(() => {
    return fareKeys.map((fareKey, index) => {
      const fare: IFareData | undefined = fares[fareKey];
      return fare != null ? (
        <FlightSlot.FlightFareGridItem
          key={`fare-grid-${fare.fareId}`}
          fare={fare}
          tripType={tripType}
          tagsLimit={tagsLimit}
          onFareGridClick={onFareGridClick}
          isFilteredOut={!(fareShelfFilter?.includes(fare.fareShelf) ?? false)}
          numberOfFares={fare.numberOfFares}
        />
      ) : (
        <div key={`no-fare-${index}`} className={cn("empty-fare")}></div>
      );
    });
  }, [
    FlightSlot,
    cn,
    fareKeys,
    fareShelfFilter,
    fares,
    onFareGridClick,
    tagsLimit,
    tripType,
  ]);
  return <ul className={clsx(block, className)}>{grid}</ul>;
};
